/**
 * useUserChallenges Hook
 * Fetches challenges visible to the current user (not admin view)
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Challenge,
  ChallengeWithStatus,
  ChallengeCompletion,
  ChallengeConfig,
} from "../types";

/** Database row type for challenges */
interface ChallengeRow {
  id: string;
  weekend_id: string | null;
  title: string;
  description: string | null;
  challenge_type: string;
  target_type: string;
  visibility: string;
  visible_to_team_id: string | null;
  visible_to_user_ids: string[] | null;
  has_points: boolean;
  points: number;
  bonus_points: number;
  available_from: string | null;
  available_until: string | null;
  time_limit_minutes: number;
  status: string;
  config: ChallengeConfig;
  linked_event_id: string | null;
  reveal_targets: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Database row type for completions */
interface CompletionRow {
  id: string;
  challenge_id: string;
  completed_by: string;
  team_id: string | null;
  submission: Record<string, unknown>;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  points_earned: number;
  bonus_earned: number;
  started_at: string | null;
  completed_at: string;
  created_at: string;
}

interface UseUserChallengesReturn {
  /** All visible challenges for user */
  challenges: ChallengeWithStatus[];
  /** Only active challenges user can complete */
  activeChallenges: ChallengeWithStatus[];
  /** Challenges with pending submissions */
  pendingChallenges: ChallengeWithStatus[];
  /** Challenges with upcoming deadlines */
  urgentChallenges: ChallengeWithStatus[];
  /** Recently added challenges (last 24h) */
  newChallenges: ChallengeWithStatus[];
  /** Completed challenges */
  completedChallenges: ChallengeWithStatus[];
  /** Total points user can earn */
  totalAvailablePoints: number;
  /** Points user has earned */
  earnedPoints: number;
  /** Badge count for new/urgent challenges */
  badgeCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for user-facing challenges
 */
export const useUserChallenges = (): UseUserChallengesReturn => {
  const { profile, activeWeekend, userTeam } = useAuth();
  const [challenges, setChallenges] = useState<ChallengeWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch challenges visible to this user
   */
  const fetchChallenges = useCallback(async () => {
    if (!profile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Only fetch active challenges for the current weekend
      let query = supabase
        .from("challenges")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (activeWeekend?.id) {
        query = query.eq("weekend_id", activeWeekend.id);
      }

      const { data: challengesData, error: challengesError } = await query;

      if (challengesError) throw challengesError;

      // Filter challenges based on visibility
      const visibleChallenges = ((challengesData || []) as ChallengeRow[]).filter((challenge) => {
        // All visibility - everyone can see
        if (challenge.visibility === "all") return true;

        // Specific team - only that team can see
        if (challenge.visibility === "specific_team") {
          return challenge.visible_to_team_id === userTeam?.id;
        }

        // Specific users - only those users can see
        if (challenge.visibility === "specific_users") {
          return challenge.visible_to_user_ids?.includes(profile.id);
        }

        return false;
      });

      // Fetch user's completions
      const challengeIds = visibleChallenges.map((c) => c.id);
      let completionsMap: Record<string, ChallengeCompletion> = {};

      if (challengeIds.length > 0) {
        let completionsQuery = supabase
          .from("challenge_completions")
          .select("*")
          .in("challenge_id", challengeIds);

        // For team challenges, also get team completions
        if (userTeam?.id) {
          completionsQuery = completionsQuery.or(
            `completed_by.eq.${profile.id},team_id.eq.${userTeam.id}`
          );
        } else {
          completionsQuery = completionsQuery.eq("completed_by", profile.id);
        }

        const { data: completionsData } = await completionsQuery;

        if (completionsData) {
          (completionsData as CompletionRow[]).forEach((completion) => {
            completionsMap[completion.challenge_id] = completion as unknown as ChallengeCompletion;
          });
        }
      }

      // Map challenges with status
      const now = new Date();
      const challengesWithStatus: ChallengeWithStatus[] = visibleChallenges.map((challenge) => {
        const completion = completionsMap[challenge.id] || null;
        const isCompleted = completion?.status === "approved" || completion?.status === "auto_approved";
        const isPending = completion?.status === "pending";

        // Check availability window
        const availableFrom = challenge.available_from ? new Date(challenge.available_from) : null;
        const availableUntil = challenge.available_until ? new Date(challenge.available_until) : null;
        const isAvailable =
          (!availableFrom || now >= availableFrom) &&
          (!availableUntil || now <= availableUntil);

        return {
          ...challenge,
          completion,
          is_completed: isCompleted,
          can_complete: isAvailable && !isCompleted && !isPending,
        } as ChallengeWithStatus;
      });

      setChallenges(challengesWithStatus);
    } catch (err: any) {
      console.error("Error fetching user challenges:", err);
      setError(err.message || "Kon uitdagingen niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [profile?.id, activeWeekend?.id, userTeam?.id]);

  // Computed values
  const activeChallenges = useMemo(() => {
    return challenges.filter((c) => c.can_complete);
  }, [challenges]);

  const pendingChallenges = useMemo(() => {
    return challenges.filter((c) => c.completion?.status === "pending");
  }, [challenges]);

  const urgentChallenges = useMemo(() => {
    const now = new Date();
    const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    return challenges.filter((c) => {
      if (!c.can_complete) return false;
      if (!c.available_until) return false;
      const deadline = new Date(c.available_until);
      return deadline <= in24h && deadline > now;
    });
  }, [challenges]);

  const newChallenges = useMemo(() => {
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);

    return challenges.filter((c) => {
      const createdAt = new Date(c.created_at);
      return createdAt >= yesterday && c.can_complete;
    });
  }, [challenges]);

  const completedChallenges = useMemo(() => {
    return challenges.filter((c) => c.is_completed);
  }, [challenges]);

  const totalAvailablePoints = useMemo(() => {
    return activeChallenges.reduce((sum, c) => sum + (c.has_points ? c.points : 0), 0);
  }, [activeChallenges]);

  const earnedPoints = useMemo(() => {
    return completedChallenges.reduce((sum, c) => {
      return sum + (c.completion?.points_earned || 0);
    }, 0);
  }, [completedChallenges]);

  const badgeCount = useMemo(() => {
    // Count new challenges + urgent challenges (unique)
    const urgentIds = new Set(urgentChallenges.map((c) => c.id));
    const newIds = new Set(newChallenges.map((c) => c.id));
    const allIds = new Set([...urgentIds, ...newIds]);
    return allIds.size;
  }, [urgentChallenges, newChallenges]);

  // Initial fetch
  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!activeWeekend?.id) return;

    const channel = supabase
      .channel("user-challenges")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "challenges",
          filter: `weekend_id=eq.${activeWeekend.id}`,
        },
        () => {
          fetchChallenges();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "challenge_completions",
        },
        () => {
          fetchChallenges();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeWeekend?.id, fetchChallenges]);

  return {
    challenges,
    activeChallenges,
    pendingChallenges,
    urgentChallenges,
    newChallenges,
    completedChallenges,
    totalAvailablePoints,
    earnedPoints,
    badgeCount,
    isLoading,
    error,
    refetch: fetchChallenges,
  };
};

export default useUserChallenges;

